import type { Copy } from "../types";

export const pl: Copy = {
    seo: {
      title: "Kalkulator wieku samochodu | Sprawdzanie wieku pojazdu według roku i miesiąca",
      description:
        "Sprawdź wiek pojazdu na podstawie roku produkcji i opcjonalnego miesiąca. Zobacz lata, miesiące, dni, łączną liczbę dni i terminy kamieni milowych.",
      keywords: [
        "kalkulator wieku samochodu",
        "kalkulator wieku pojazdu",
        "rok produkcji samochodu",
        "miesiąc produkcji pojazdu",
        "ile lat ma mój samochód",
        "wiek samochodu według roku",
        "sprawdzanie wieku pojazdu",
        "kalkulator wieku dowodu rejestracyjnego",
        "wiek samochodu według miesiąca",
        "kalkulator daty pojazdu",
        "alternatywa mParivahan",
        "alternatywa Parivahan",
        "kalkulator wieku pojazdu RTO",
        "sprawdzanie wieku rejestracji samochodu",
        "wiek rejestracji pojazdu",
      ],
      faq: [
        {
          question: "Co robi ten kalkulator?",
          answer:
            "Oblicza dokładny wiek pojazdu z wybranego roku produkcji i opcjonalnego miesiąca, a następnie pokazuje lata, miesiące, dni i kamienie milowe.",
        },
        {
          question: "Po co opcjonalny miesiąc?",
          answer:
            "Miesiąc daje dokładniejsze obliczenie wieku. Jeśli go pominiesz, aplikacja użyje stycznia wybranego roku.",
        },
        {
          question: "Czy mogę wybrać przyszły rok lub miesiąc?",
          answer:
            "Nie. Przyszłe lata są blokowane, a przyszłe miesiące są wyłączone, gdy wybrany rok jest rokiem bieżącym.",
        },
      ],
    },
    headerTagline: "Narzędzie dla pojazdów",
    headerTitle: "Kalkulator wieku samochodu",
    reset: "Resetuj",
    switchToLight: "Przełącz na tryb jasny",
    switchToDark: "Przełącz na tryb ciemny",
    languageLabel: "Język",
    languageEnglish: "Angielski",
    languageHindi: "Hindi",
    heroEyebrow: "Wiek auta, jasno",
    heroTitle: "Proste narzędzie daty dla wieku pojazdu.",
    heroDescription:
      "Wybierz rok produkcji. Dodaj miesiąc tylko wtedy, gdy chcesz dokładniejszy wynik wieku.",
    yearSelectLabel: "Rok produkcji",
    monthSelectLabel: "Miesiąc produkcji",
    includeMonthLabel: "Uwzględnij konkretny miesiąc dla większej dokładności",
    includeMonthNote:
      "Jeśli nie podasz miesiąca, do obliczeń zostanie użyty styczeń.",
    yearOnlyBadge: "Tylko rok",
    yearMonthBadge: "Rok + miesiąc",
    yearPlaceholder: "Wybierz rok",
    monthPlaceholder: "Wybierz miesiąc",
    inputTitle: "Rok produkcji",
    inputLabel: "Rok produkcji",
    blockedDateNote: "Przyszłe lata są zablokowane.",
    result: "Wynik",
    vehicleAgeLead: "Twój pojazd jest",
    asOfToday: "Na dziś",
    years: "Lata",
    months: "Miesiące",
    days: "Dni",
    totalDays: (count) => `${count.toLocaleString("pl-PL")} dni łącznie`,
    ageLabels: {
      almostNew: "Prawie nowy",
      modern: "Nowoczesny",
      mature: "Dojrzały",
      classic: "Klasyczny",
    },
    estimatedValue: {
      high: "Wysoki potencjał odsprzedaży",
      healthy: "Dobra utrata wartości",
      moderate: "Umiarkowana amortyzacja",
      collector: "Może rosnąć zainteresowanie kolekcjonerów",
    },
    maintenanceTitle: "Konserwacja",
    maintenance: {
      routine: "Rutynowy serwis i utrzymanie OEM",
      watch: "Kontroluj hamulce, płyny i opony",
      plan: "Zaplanuj części eksploatacyjne i przeglądy",
      specialized: "Specjalistyczna obsługa staje się wartościowa",
    },
    registrationTitle: "Rejestracja",
    registration: {
      collector: "W niektórych regionach może kwalifikować się do traktowania kolekcjonerskiego",
      classic: "W niektórych regionach może obowiązywać rejestracja pojazdu klasycznego",
      standard: "Spodziewaj się standardowych odnowień i kontroli regionalnych",
      regular: "Zwykle obowiązuje standardowa rejestracja",
    },
    upcomingMilestoneTitle: "Nadchodzący kamień milowy",
    milestoneTimelineTitle: "Oś czasu kamieni milowych",
    milestoneRemaining: ({ label, days }) => `${label} za ${days.toLocaleString("pl-PL")} dni`,
    reached: "Osiągnięto",
    upcoming: "Nadchodzące",
    allMilestonesReachedTitle: "Osiągnięto wszystkie wymienione kamienie milowe",
    allMilestonesReachedBody:
      "Ten pojazd przekroczył zakres kamieni milowych pokazanych obecnie w aplikacji.",
    copySummary: "Kopiuj podsumowanie",
    shareResult: "Udostępnij wynik",
    resetMessage: "Kalkulator zresetowany",
    summaryCopied: "Podsumowanie skopiowane do schowka",
    clipboardFailed: "Dostęp do schowka nie powiódł się",
    emptyTitle: "Wybierz rok produkcji, aby zobaczyć pełne zestawienie",
    emptyDescription:
      "Po wybraniu roku zobaczysz dokładny wiek, łączną liczbę dni, notatki serwisowe, status klasyka i terminy kamieni milowych.",
    shareTitle: "Kalkulator wieku samochodu",
    summary: ({ label, years, months, days, asOf }) =>
      `Mój samochód został wyprodukowany w ${label} i na dzień ${asOf} ma ${years} lat, ${months} miesięcy i ${days} dni.`,
    milestones: {
      1: { label: "Pierwszy rok", note: "Przydatne do planowania gwarancji i serwisu." },
      3: { label: "Trzy lata", note: "Częsty moment zmian wartości i odsprzedaży." },
      5: { label: "Pięć lat", note: "Dobry czas na dokładniejsze planowanie kosztów utrzymania." },
      10: { label: "Dziesięć lat", note: "Starzejące się podzespoły wymagają dodatkowej uwagi." },
      15: { label: "Piętnaście lat", note: "Wiele regionów zaczyna tu traktować pojazd jak klasyczny." },
      25: { label: "Dwadzieścia pięć lat", note: "W niektórych rynkach rośnie atrakcyjność kolekcjonerska i importowa." },
    },
  };
